import moment from 'moment';
import { Item, List, Section, IResultDisplay } from '@/plugins/returnDisplay';
import { trans } from '@/i18n';
import { TitleCase } from '@/plugins/utilities';

interface BlackboxMetadata {
    age: number;
    timestamp: Date;
}

interface Flash {
    installed: boolean;
    version: string;
}

interface Browser {
    cookiesEnabled: boolean;
    configuredLanguage: string;
    flash: Flash;
    language: string;
    type: string;
    timezone: string;
    version: string;
    privateBrowsing: boolean;
}

interface RegistrationResult {
    matchStatus: string;
}

interface Device {
    alias: number;
    blackboxMetadata: BlackboxMetadata;
    browser: Browser;
    firstSeen: Date;
    isNew: boolean;
    os: string;
    registrationResult: RegistrationResult;
    screen: string;
    type: string;
}

interface IpLocation {
    city: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    region: string;
}

interface IpAddress {
    address: string;
    isp: string;
    ipLocation: IpLocation;
    parentOrganization: string;
    source: string;
    ipSignal: IpSignal;
}

interface IpSignal {
    score: number;
    resultCodes: string[];
}

interface Rule {
    type: string;
    reason: string;
    score: number;
}

interface RuleResults {
    score: number;
    rulesMatched: number;
    rules: Rule[];
}

interface SureScoreModel {
    context: string;
    modelVersion: string;
    score: number;

}

interface MlValue1 {
    value: number;
}

interface MachineLearning {
    mlValue1: MlValue1;
    "model1-primary": SureScoreModel;
    "model1-preview": SureScoreModel;
    "model2-primary": SureScoreModel;
    "model2-preview": SureScoreModel;
    "model3-primary": SureScoreModel;
    "model3-preview": SureScoreModel;
}

interface Email {
    address: string;
    containsDomainIssue: boolean;
    containsMailboxIssue: boolean;
    containsSyntaxIssue: boolean;
    age: string;
    dateFirstSeen: Date;
    daysSinceDateFirstSeen: number;
    velocity: string;
    popularity: string;
    disposable: boolean;
    domainNotAllowed: boolean;
    userNameNotAllowed: boolean;
}

interface Phone {
    number: string;
    riskScore: number;
    type: string[];
}

interface Verification {
    email: Email[];
    phone: Phone[];
}

interface Details {
    device: Device;
    statedIp: IpAddress;
    realIp: IpAddress;
    verification: Verification;
    ruleResults: RuleResults;
    machineLearning: MachineLearning;
}

interface CkBody {
    deviceType: string;
    matchStatus: string;
    measureOfChange: string;
    subscriberId: number;
    userAccountCode: string;
    regDate?: Date;
    ttlSeconds?: number;
    ttlType?: number;
    count: number;
}

interface CkHead {
    apiAccount: string;
    code: string;
    responseType: string;
    result: string;
    status: number;
    subscriberId: string;
    transactionId: string;
    version: number;
}

interface ClearKey {
    body: CkBody;
    head: CkHead;
    version: number;
}

export interface IFraudForceResult {
    id: string;
    result: string;
    reason: string;
    statedIp: string;
    accountCode: string;
    trackingNumber: number;
    details: Details;
    clearKey: ClearKey | null;
}

export class FraudForceResult implements IFraudForceResult {
    public id: string;
    public result: string;
    public reason: string;
    public statedIp: string;
    public accountCode: string;
    public trackingNumber: number;
    public details: Details;
    public clearKey: ClearKey | null;

    constructor(args: IFraudForceResult) {
        this.id = args.id;
        this.result = args.result;
        this.reason = args.reason;
        this.statedIp = args.statedIp;
        this.accountCode = args.accountCode;
        this.trackingNumber = args.trackingNumber;
        this.details = args.details;
        if (args.clearKey) {
            this.clearKey = args.clearKey;
        } else {
            this.clearKey = null;
        }
    }

    public formatClearKey(color: string): IResultDisplay {
        const out = ({
            color,
            title: '<strong>ClearKey</strong><span class="font-weight-regular"> | Device-Based Authentication</span>',
            result: {
                color,
                text: '',
                score: '0',
                sections: new Array<Section>(),
            },
        } as IResultDisplay);
        const device = new Array<Item>();
        if (this.clearKey) {
            if (this.clearKey.body.matchStatus) {
                device.push({
                    name: TitleCase(trans('iovation.clearkey.status').toString()),
                    value: this.clearKey.body.matchStatus,
                });
                if (this.clearKey.body.matchStatus.indexOf('NONE') > -1) {
                    out.result.color = 'warning';
                    out.result.score = TitleCase(trans('standard.not_registered').toString());
                }
            }
            if (this.clearKey.body.userAccountCode) {
                device.push({
                    name: TitleCase(trans('standard.user').toString()),
                    value: this.clearKey.body.userAccountCode,
                });
            }
            if (this.clearKey.body.deviceType) {
                device.push({
                    name: TitleCase(trans('iovation.clearkey.devicetype').toString()),
                    value: this.clearKey.body.deviceType,
                });
            }
            if (this.clearKey.body.measureOfChange) {
                out.result.score = this.clearKey.body.measureOfChange.toUpperCase();
                switch (this.clearKey.body.measureOfChange.toUpperCase()) {
                    case 'LOW':
                        out.result.color = 'success';
                        break;
                    case 'MED':
                        out.result.color = 'warning';
                        break;
                    default:
                        out.result.color = 'error';
                        break;
                }
                device.push({
                    name: TitleCase(trans('iovation.clearkey.change').toString()),
                    value: this.clearKey.body.measureOfChange.toLowerCase(),
                });
            }
            if (this.clearKey.body.count) {
                device.push({
                    name: TitleCase(trans('iovation.clearkey.number').toString()),
                    value: this.clearKey.body.count.toString(),
                });
            }
            if (this.clearKey.body.ttlType) {
                out.result.color = 'success';
                out.result.score = TitleCase(trans('iovation.clearkey.registered').toString());
            }
            if (this.clearKey.body.count) {
                out.result.color = 'error';
                out.result.score = TitleCase(trans('iovation.clearkey.removal').toString());
            }
        }
        out.result.sections.push({
            title: TitleCase(trans('iovation.clearkey.associate').toString()),
            items: device,
        });
        return out;
    }

    public formatDeviceCollection(color: string): IResultDisplay {

        const out = ({
            color,
            title: '<strong>Risk</strong><span class="font-weight-regular"> | Device Risk Collection</span>',
            result: {
                color: this.resultColor(),
                text: "",
                score: "",
                sections: new Array<Section>(),
            },
        } as IResultDisplay);
        
        out.result.sections.push({
            title: TitleCase(trans('iovation.fraudforce.device').toString()),
            items: this.getDeviceData(this.details),
        });

        out.result.sections.push({
            title: "Network Info - Device Risk",
            items: this.getNetworkData("Device Risk", this.details?.realIp),
        });

        return out;
    }

    public format(color: string): IResultDisplay {
        const out = ({
            color,
            title: '<strong>Device Risk</strong><span class="font-weight-regular"> | Device-Based Reputation</span>',
            result: {
                color: this.resultColor(),
                text: this.toResult(),
                score: this.details.ruleResults.score.toString(),
                sections: new Array<Section>(),
            },
        } as IResultDisplay);

        out.result.sections.push({
            title: TitleCase(trans('iovation.fraudforce.device').toString()),
            items: this.getDeviceData(this.details),
        });

        out.result.sections.push({
            title: "Network Info - Device Risk",
            items: this.getNetworkData("Device Risk", this.details?.realIp),
        });

        out.result.sections.push({
            title: "Network Info - Stated IP (Mocked for Demo Purposes)",
            items: this.getNetworkData("Stated", this.details?.statedIp),
        });

        const rules = {
            name: `Rules (${this.details.ruleResults.rulesMatched})`,
            items: this.toRules(),
        };
        out.result.sections.push({
            title: TitleCase(trans('iovation.fraudforce.bizrules').toString()),
            list: rules,
        });
        const email = this.emailValidation();
        if (email) {
            out.result.sections.push({
                title: TitleCase(trans('iovation.fraudforce.emailvalid').toString()),
                list: email,
            });
        }
        const phone = this.phoneValidation();
        if (phone) {
            out.result.sections.push({
                title: TitleCase(trans('iovation.fraudforce.phonevalid').toString()),
                list: phone,
            });
        }

        const ss = [
            {
                name: "Enhanced SureScore Primary",
                value: this.details?.machineLearning?.["model1-primary"]?.score.toString() || "N/A",
            },
            {
                name: "Enhanced SureScore Preview",
                value: this.details?.machineLearning?.["model1-preview"]?.score.toString() || "N/A",
            },
            {
                name: "Advanced Transaction Model Primary",
                value: this.details?.machineLearning?.["model2-primary"]?.score.toString() || "N/A",
            },
            {
                name: "Advanced Transaction Model Preview",
                value: this.details?.machineLearning?.["model2-preview"]?.score.toString() || "N/A",
            },
            {
                name: "Behavioral Model Primary",
                value: this.details?.machineLearning?.["model3-primary"]?.score.toString() || "N/A",
            },
            {
                name: "Behavioral Model Preview",
                value: this.details?.machineLearning?.["model3-preview"]?.score.toString() || "N/A",
            },
        ];

        out.result.sections.push({
            title: 'ML Model Score(s)',
            items: ss,
        });
        return out;
    }

    private getLanguage(languageCode: string) {
        let languageName: string = "N/A";
        try {
            if (languageCode) {
                // @ts-ignore
                const displayNames = new Intl.DisplayNames(['en'], {type: 'language'});
                languageName = displayNames.of(languageCode);
            }
        } catch (e) {
            return "N/A"
        }
        return languageName;
    }

    private emailValidation(): List | null {
        const a = ({} as List);
        if (this.details.verification) {
            if (this.details.verification.email.length > 0) {
                a.name = this.details.verification.email[0].address;
                a.items = new Array<Item>();
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.domain').toString()),
                    value: this.toBoolean(this.details.verification.email[0].containsDomainIssue),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.mailbox').toString()),
                    value: this.toBoolean(this.details.verification.email[0].containsMailboxIssue),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.syntax').toString()),
                    value: this.toBoolean(this.details.verification.email[0].containsSyntaxIssue),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.age').toString()),
                    value: this.details.verification.email[0].age,
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.velocity').toString()),
                    value: this.details.verification.email[0].velocity,
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.popular').toString()),
                    value: this.details.verification.email[0].popularity,
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.dispose').toString()),
                    value: this.toBoolean(this.details.verification.email[0].disposable),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.spam').toString()),
                    value: this.toBoolean(this.details.verification.email[0].domainNotAllowed),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.userspam').toString()),
                    value: this.toBoolean(this.details.verification.email[0].userNameNotAllowed),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.email.firstseen').toString()),
                    value: `${this.toDate(this.details.verification.email[0].dateFirstSeen)} (${this.details.verification.email[0].daysSinceDateFirstSeen})`,
                });
                return a;
            }
        }
        return null;
    }

    private phoneValidation(): List | null {
        const a = ({} as List);
        if (this.details.verification) {
            if (this.details.verification.phone.length > 0) {
                a.name = this.details.verification.phone[0].number.toString();
                a.items = new Array<Item>();
                a.items.push({
                    name: TitleCase(trans('idvision.origination.phone.score').toString()),
                    value: this.details.verification.phone[0].riskScore.toString(),
                });
                a.items.push({
                    name: TitleCase(trans('idvision.origination.phone.type').toString()),
                    value: this.details.verification.phone[0].type[0],
                });
                return a;
            }
        }
        return null;
    }

    private didRuleTrigger(ruleType: string, rules: Array<Rule>): boolean {
        return rules.some((rule) => rule.type === ruleType)
    }

    private toRules(): Item[] {
        const a = new Array<Item>();
        this.details.ruleResults.rules.forEach((r) => {
            a.push({
                name: r.type,
                value: `(${r.score}) ${r.reason}`,
            });
        });
        return a;
    }

    private getDeviceData(details: any): Array<Item> {

        const device = new Array<Item>();

        device.push({
            name: "Device Id",
            value: this.showNA(details?.device?.alias?.toString()),
        });

        device.push({
            name: "Browser",
            value: this.showNA(details?.device?.browser?.type?.toString())
        });

        device.push({
            name: "Operating System",
            value: this.showNA(details?.device?.os.toString())
        });

        device.push({
            name: "Screen Size",
            value: this.showNA(details?.device?.screen.toString())
        });

        device.push({
            name: "Abnormal Screen Color Depth",
            value: this.toBoolean(this.didRuleTrigger("Screen Color Depth", details?.ruleResults?.rules))
        });

        device.push({
            name: "Languages",
            value: `${this.getLanguage(details?.device?.browser?.language)}`
        });

        device.push({
            name: "Timezone",
            value: `${Intl.DateTimeFormat().resolvedOptions().timeZone}`
        });

        device.push({
            name: "Bot Detected",
            value: this.toBoolean(this.didRuleTrigger("Bot Detection", details?.ruleResults?.rules))
        });

        device.push({
            name: "Proxy Identified",
            value: this.toBoolean(this.didRuleTrigger("Proxy In Use", details?.ruleResults?.rules))
        });

        device.push({
            name: "Virtual Environment in Use",
            value: this.toBoolean(this.didRuleTrigger("Virtual Renderer", details?.ruleResults?.rules))
        });

        device.push({
            name: "High Velocity Device",
            value: this.toBoolean(this.didRuleTrigger("Transactions Per Device (local)", details?.ruleResults?.rules))
        });

        const rddResult = (details?.device?.remoteDetected === true) ? "Detected" : "Not Detected";
        device.push({
            name: "Remote Tool",
            value: rddResult
        });

        device.push({
            name: "Private Browser in Use",
            value: this.toBoolean(this.didRuleTrigger("Private Browsing Detected", details?.ruleResults?.rules))
        });

        device.push({
            name: TitleCase(trans('iovation.fraudforce.firstseen').toString()),
            value: this.toDate(details?.device?.firstSeen),
        });
        device.push({
            name: TitleCase(trans('iovation.fraudforce.newdevice').toString()),
            value: this.toBoolean(details?.device?.isNew),
        });

        return device;

    }
    
    private getNetworkData(source: string, ipGeo: IpAddress): Array<Item> {

        return [
            {
                name: `${source} IP`,
                value: this.showNA(ipGeo.address),
            },
            {
                name: `${source} ISP`,
                value: this.showNA(ipGeo?.isp)
            },
            {
                name: `${source} City`,
                value: this.showNA(ipGeo?.ipLocation?.city)
            },
            {
                name: `${source} Region`,
                value: this.showNA(ipGeo?.ipLocation?.region)
            },
            {
                name: `${source} Country`,
                value: this.showNA(ipGeo?.ipLocation?.country)
            },
            {
                name: `${source} IP Signal`,
                value: this.getIpSignalLabel(ipGeo?.ipSignal?.score?.toString()),
                className: this.getIpSignalClass(ipGeo?.ipSignal?.score?.toString()),
            },
            {
                name: `${source} IP Signal Result Code(s)`,
                value: ipGeo?.ipSignal?.resultCodes?.toString() || "N/A",
            },

        ];
    }

    private toNetwork(net: any): string {
        let o = `${net.address}`;
        o += `, ${net.isp || 'No ISP Info'}`;
        if (net.ipLocation) {
            o += `, ${net.ipLocation.city}, ${net.ipLocation.region}, ${net.ipLocation.country}`;
        }
        return o;
    }

    private toResult(): string {
        switch (this.result.toLowerCase()) {
            case 'a':
                return TitleCase(trans('iovation.fraudforce.allow').toString());
            case 'r':
                return TitleCase(trans('iovation.fraudforce.review').toString());
            case 'd':
                return TitleCase(trans('iovation.fraudforce.deny').toString());
            default:
                return TitleCase(trans('iovation.fraudforce.allow').toString());
        }
    }

    private getIpSignalClass(signal: string) : string {
        switch (signal) {
            case "1":
                return "very-low-risk";
            case "2":
                return "low-risk";
            case "3":
                return "medium-risk";
            case "4":
                return "high-risk";
            case "5":
                return "very-high-risk";
            default:
                return "N/A";
        }
    }
    
    private getIpSignalLabel(signal: string) : string {
        switch (signal) {
            case "1":
                return "1 - Very Low Risk";
            case "2":
                return "2 - Low Risk";
            case "3":
                return "3 - Medium Risk";
            case "4":
                return "4 - High Risk";
            case "5":
                return "5 - Very High Risk";
            default:
                return "N/A";
        }
    }

    private resultColor(): string {
        try {
            if (this.result) {
                switch (this.result.toLowerCase()) {
                    case 'a':
                        return 'success';
                    case 'r':
                        return 'warning';
                    default:
                        return 'error';
                }
            }
            return 'success';
        } catch (err) {
            return 'success';
        }
    }

    private toDate(dt: Date): string {
        return moment(dt).fromNow();
    }

    private toBoolean(t: boolean): string {
        if(t === null || t === undefined) {
            return "N/A"
        } else {
            return t ? 'Yes' : 'No';
        }
    }

    private showNA(t:any): any {
        if(t === null || t === "" || t === undefined) {
            return "N/A"
        } else {
            return t
        }
    }


}
